import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog, MatDialogRef, MatSnackBar } from '@angular/material';

import { FuseConfirmDialogComponent } from '@fuse/components/confirm-dialog/confirm-dialog.component';

import { fuseAnimations } from '@fuse/animations';

import { CuentaFormDialogComponent } from "app/main/gastos/finanzas/cuenta-form/cuenta-form.component";

import { CategoriesExpensesService } from "app/main/gastos/categories-expenses/categories-expenses.service";
import { FinanzasCuentasService } from 'app/main/gastos/finanzas/cuentas.service';
import { Router } from '@angular/router';

import { Store } from "store";


@Component({
    selector: 'finanzas-cuentas',
    templateUrl: './cuentas.component.html',
    styleUrls: ['./cuentas.component.scss'],
    animations: fuseAnimations
})
export class FinanzasCuentasComponent implements OnInit, OnDestroy {
    dialogRef: any;

    categories: any[];
    currencies: any[];
    cuentas: any[];
    cuentasFilteredByCategory: any[];
    filteredCuentas: any[];
    currentCategory: string;
    searchTerm: string;
    confirmDialogRef: MatDialogRef<FuseConfirmDialogComponent>;

    // Private
    private _unsubscribeAll: Subject<any>;

    /**
     * Constructor
     *
     * @param {AcademyCoursesService} _finanzasCuentasService
     */
    constructor(
        private router: Router,
        private _finanzasCuentasService: FinanzasCuentasService,
        private _matDialog: MatDialog,
        private _matSnackBar: MatSnackBar,
        private store: Store
    ) {
        // Set the defaults
        this.currentCategory = 'all';
        this.searchTerm = '';

        // Set the private defaults
        this._unsubscribeAll = new Subject();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to categories
        this._finanzasCuentasService.onCategoriesChanged
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(categories => {
                this.categories = categories;
            });

        // Subscribe to currencies
        this._finanzasCuentasService.onCurrenciesChanged
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(currencies => {
                this.currencies = currencies;
            });    

        // Subscribe to courses
        this._finanzasCuentasService.onCoursesChanged
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(cuentas => {
                this.filteredCuentas = this.cuentasFilteredByCategory = this.cuentas = cuentas;
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Filter courses by category
     */
    filterCoursesByCategory(): void {
        // Filter
        if (this.currentCategory === 'all') {
            this.cuentasFilteredByCategory = this.cuentas;
            this.filteredCuentas = this.cuentas;
        }
        else {
            this.cuentasFilteredByCategory = this.cuentas.filter((course) => {
                return course.category === this.currentCategory;
            });

            this.filteredCuentas = [...this.cuentasFilteredByCategory];

        }

        // Re-filter by search term
        this.filterCoursesByTerm();
    }

    /**
     * Filter courses by term
     */
    filterCoursesByTerm(): void {
        const searchTerm = this.searchTerm.toLowerCase();

        // Search
        if (searchTerm === '') {
            this.filteredCuentas = this.cuentasFilteredByCategory;
        }
        else {
            this.filteredCuentas = this.cuentasFilteredByCategory.filter((account) => {
                return account.nombre.toLowerCase().includes(searchTerm);
            });
        }
    }

    /** 
    * Add new account 
    */
    addNewAccount() {
        // console.log('Open modal New Account')
        this.dialogRef = this._matDialog.open(CuentaFormDialogComponent, {
            panelClass: 'cuenta-form-dialog',
            data: {
                action: 'new',
                categories: this.categories,
                currencies: this.currencies
            }
        });

        this.dialogRef.afterClosed()
            .subscribe((response: FormGroup) => {
                if (!response) {
                    return;
                }

                
                this._finanzasCuentasService.AddCuenta(response.getRawValue())
                    .subscribe(res => {
                        this._matSnackBar.open('La cuenta fue creada exitosamente', '', {
                            duration: 3000,
                        });
                    }, (err) => {
                        console.log('Error Add Account ', err);
                        this._matSnackBar.open('Error al crear la cuenta intente de nuevo', '', {
                            duration: 5000,
                        });
                    });
            });
    }

    editAccount(account) {
        this.dialogRef = this._matDialog.open(CuentaFormDialogComponent, {
            panelClass: 'cuenta-form-dialog',
            data: {
                account: account,
                categories: this.categories,
                currencies: this.currencies,
                action: 'edit'
            }
        });

        this.dialogRef.afterClosed()
            .subscribe(response => {
                if (!response) {
                    return;
                }
                const actionType: string = response[0];
                const formData: FormGroup = response[1];
                switch (actionType) {
                    /**
                     * Save
                     */
                    case 'save':

                        this._finanzasCuentasService.updateAccount(formData.getRawValue())
                            .subscribe(res => {
                                // console.log('Se elimino')
                                this._matSnackBar.open('La cuenta fue actualizada exitosamente', '', {
                                    duration: 3000,
                                });
                            }, (err) => {
                                this._matSnackBar.open('Error al actulizar la cuenta intente de nuevo', '', {
                                    duration: 5000,
                                });
                            });

                        break;
                    /**
                     * Delete
                     */
                    case 'delete':

                        this.deleteAccount(account);

                        break;
                }
            });
    }

    deleteAccount(account) {

        this.confirmDialogRef = this._matDialog.open(FuseConfirmDialogComponent, {
            disableClose: false
        });

        this.confirmDialogRef.componentInstance.confirmMessage = '¿Quieres eliminar esta cuenta?';
        this.confirmDialogRef.componentInstance.informationMessage = 'Si elimina esta cuenta perdera todos sus registros';

        this.confirmDialogRef.afterClosed().subscribe(result => {
            if (result) {
                // this._contactsService.deleteContact(contact);
                this._finanzasCuentasService.deleteAccount(account)
                    .subscribe(res => {
                        this._matSnackBar.open('La cuenta fue eliminada', '', {
                            duration: 3000,
                        });
                    }, (err) => {
                        this._matSnackBar.open('Error al eliminar la cuenta intente de nuevo', '', {
                            duration: 5000,
                        });
                    });

            }
            this.confirmDialogRef = null;
        });
    }

    onRedirectSpends(account) {
        let a: Account = account;
        this.store.set('account', a);
        this.router.navigate(['/gastos/registros/movimientos/',  a.id])
    }
}
